import React from 'react';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import { selectProject } from '../../features/project/projectSlice';
import { unitStatusTitles } from '../../utils/Constants';
import { getSalesStatusCodes } from '../../utils/Functions';
import { IStatus } from '../../types/project';
import { theme } from '../../theme';

Chart.register(ArcElement, Tooltip, Legend);
const DoughnutChart = (props: ChildProps) => {
  const { statusData } = props;
  const project = useSelector(selectProject);
  const allStatus: IStatus[] = [...project.status, ...unitStatusTitles];
  const salesStatusCodes = getSalesStatusCodes(project);

  const data = {
    labels: statusData?.allTotals.map((status: any) => {
      let selectedStatus = allStatus.find((all: IStatus) => all.code === status._id);
      if (selectedStatus) {
        return selectedStatus.name;
      } else return '#000';
    }),
    datasets: [
      {
        data: statusData.allTotals.map((status: any) => status.count),
        backgroundColor: statusData.allTotals.map((status: any) => {
          let selectedStatus = allStatus.find((all: IStatus) => all.code === status._id);
          if (selectedStatus) {
            return selectedStatus.color;
          } else return '#000';
        }),
        borderColor: ['black'],
        borderWidth: 2,
      },
    ],
  };

  const getMortgage = () => {
    let getFirmTotal = statusData.statusTotals
      .filter((all: any) => salesStatusCodes.includes(all._id) && all._id !== 'C')
      .reduce((sum: any, record: any) => sum + record.count, 0);

    if (getFirmTotal) {
      return `${statusData.mortgageTotal} / ${getFirmTotal} (${((statusData.mortgageTotal / getFirmTotal) * 100).toFixed(2)}%)`;
    } else return 'N/A';
  };

  const legendItems = data.labels.map((label: string, index: number) => ({
    label,
    color: data.datasets[0].backgroundColor[index],
    count: data.datasets[0].data[index],
  }));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: '#1a1a1a',
            '@media (max-width:1499.98px)': { fontSize: '0.88rem' },
          }}
        >
          Unit Distribution
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 3 },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          minHeight: 0,
        }}
      >
        <Box sx={{ flex: '1 1 auto', width: '100%', height: '100%', minHeight: { xs: 180, sm: 0 }, minWidth: 0, position: 'relative' }}>
          <Doughnut
            data={data}
            options={{
              maintainAspectRatio: false,
              layout: {
                autoPadding: true,
              },
              plugins: {
                legend: {
                  display: false,
                },
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            flexShrink: 0,
            maxWidth: { xs: '100%', sm: '160px' },
            maxHeight: '100%',
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          {legendItems.map((item: { label: string; color: string; count: number }, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: item.color, flexShrink: 0, border: '1px solid #000' }} />
              <Typography
                sx={{ fontSize: '0.8rem', color: '#333', lineHeight: 1.3, '@media (max-width:1499.98px)': { fontSize: '0.64rem' } }}
              >
                {item.label} ({item.count})
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          mx: { xs: 2, sm: 3 },
          mt: 2,
          mb: { xs: 2, sm: 3 },
          pt: 2,
          borderTop: `2px solid ${theme.palette.secondary.main}`,
        }}
      >
        <Typography sx={{ fontSize: '0.9rem', color: '#333', '@media (max-width:1499.98px)': { fontSize: '0.72rem' } }}>
          Mortgage <strong>{getMortgage()}</strong>
        </Typography>
      </Box>
    </Box>
  );
};

interface ChildProps {
  statusData: any;
}

export default DoughnutChart;
